import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { nightAuditApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import {
  Moon, CalendarClock, Play, Eye, CheckCircle2, AlertTriangle,
  TrendingUp, BedDouble, DollarSign, ArrowRight, RefreshCw, Loader2,
} from 'lucide-react';

const ETB = (n) =>
  `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatCard({ icon: Icon, label, value, sub, tone = 'default' }) {
  const tones = {
    default: 'text-slate-700 bg-slate-100',
    green: 'text-emerald-700 bg-emerald-100',
    amber: 'text-amber-700 bg-amber-100',
    blue: 'text-blue-700 bg-blue-100',
  };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
          <p className="text-lg font-semibold text-slate-900 truncate">{value}</p>
          {sub != null && <p className="text-xs text-slate-500 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function NightAuditPage() {
  const { currentProperty } = useAuth();
  const [loading, setLoading] = useState(true);
  const [businessDate, setBusinessDate] = useState(null);
  const [propertyName, setPropertyName] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);

  const loadBusinessDate = useCallback(async () => {
    if (!currentProperty) return;
    try {
      const res = await nightAuditApi.getBusinessDate(currentProperty.id);
      setBusinessDate(res.data.current_business_date);
      setPropertyName(res.data.property_name || '');
      setPreview(null);
    } catch (e) {
      toast.error('Failed to load business date');
    } finally {
      setLoading(false);
    }
  }, [currentProperty]);

  const loadRuns = useCallback(async () => {
    if (!currentProperty) return;
    try {
      const res = await nightAuditApi.listRuns(currentProperty.id);
      setRuns(res.data || []);
    } catch (e) {
      setRuns([]);
    }
  }, [currentProperty]);

  useEffect(() => {
    loadBusinessDate();
    loadRuns();
  }, [loadBusinessDate, loadRuns]);

  const runPreview = async () => {
    if (!currentProperty) return;
    try {
      setPreviewing(true);
      const res = await nightAuditApi.preview(currentProperty.id);
      setPreview(res.data);
      toast.success('Pre-audit checks complete');
    } catch (e) {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const runAudit = async () => {
    if (!currentProperty) return;
    try {
      setRunning(true);
      const res = await nightAuditApi.run(currentProperty.id, true);
      setLastResult(res.data);
      setConfirmOpen(false);
      setResultOpen(true);
      toast.success(`Night audit complete — business date advanced to ${res.data.next_business_date}`);
      setPreview(null);
      await loadBusinessDate();
      await loadRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Night audit failed');
    } finally {
      setRunning(false);
    }
  };

  if (!currentProperty) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-8 text-center text-slate-500">
          Select a property to run the night audit.
        </CardContent></Card>
      </div>
    );
  }

  const s = preview?.summary;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
            <Moon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Night Audit</h1>
            <p className="text-sm text-slate-500">End-of-day reconciliation &amp; business-date rollover</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => { loadBusinessDate(); loadRuns(); }}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Business date banner */}
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <CalendarClock className="h-8 w-8 text-indigo-600" />
            <div>
              <p className="text-xs uppercase tracking-wide text-indigo-700">Current Business Date</p>
              {loading ? (
                <Skeleton className="h-7 w-40 mt-1" />
              ) : (
                <p className="text-2xl font-bold text-slate-900">{businessDate || '—'}</p>
              )}
              <p className="text-xs text-slate-500">{propertyName}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={runPreview} disabled={previewing || loading}>
              {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
              Run Pre-Checks
            </Button>
            <Button onClick={() => setConfirmOpen(true)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              <Play className="h-4 w-4 mr-2" /> Run Night Audit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview results */}
      {preview && s && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Eye className="h-4 w-4" /> Pre-Audit Summary for {preview.business_date}
            <Badge variant="secondary">advances to {preview.next_business_date}</Badge>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={BedDouble} label="In-House Rooms" value={s.in_house_rooms} sub={`${s.rooms_available} available`} tone="blue" />
            <StatCard icon={TrendingUp} label="Occupancy" value={`${s.occupancy_percent}%`} sub={`OOO: ${s.rooms_out_of_order}`} tone="green" />
            <StatCard icon={DollarSign} label="Projected Room Rev." value={ETB(s.projected_room_revenue)} sub={`+ tax ${ETB(s.projected_room_tax)}`} />
            <StatCard icon={DollarSign} label="ADR / RevPAR" value={`${ETB(s.adr)}`} sub={`RevPAR ${ETB(s.revpar)}`} />
            <StatCard icon={CheckCircle2} label="Room Charges to Post" value={s.room_charges_to_post} tone="amber" />
            <StatCard icon={ArrowRight} label="Arrivals / Departures" value={`${s.arrivals} / ${s.departures}`} />
            <StatCard icon={AlertTriangle} label="No-shows to Flag" value={s.no_shows_to_flag} tone="amber" />
            <StatCard icon={DollarSign} label="Outstanding Balance" value={ETB(preview.outstanding_balances?.total_outstanding)} sub={`${preview.outstanding_balances?.with_balance} folio(s)`} />
          </div>
          {preview.no_shows?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">No-shows to be flagged</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Confirmation</TableHead><TableHead>Guest</TableHead><TableHead>Arrival</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {preview.no_shows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.confirmation_number}</TableCell>
                        <TableCell>{r.guest_name}</TableCell>
                        <TableCell>{r.arrival_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Run history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audit Run History</CardTitle>
          <CardDescription>Most recent end-of-day runs for this property</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No audit runs yet. Run the night audit to see history here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Room Charges</TableHead>
                  <TableHead>Total Revenue</TableHead>
                  <TableHead>Occupancy</TableHead>
                  <TableHead>Run At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.business_date}</TableCell>
                    <TableCell><Badge className="bg-emerald-100 text-emerald-700">{r.status}</Badge></TableCell>
                    <TableCell>{r.totals?.room_charges_posted ?? '—'}</TableCell>
                    <TableCell>{ETB(r.totals?.total_revenue)}</TableCell>
                    <TableCell>{r.totals?.occupancy_percent ?? 0}%</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Run Night Audit?
            </DialogTitle>
            <DialogDescription>
              This will post room &amp; tax charges to in-house folios, flag no-shows, roll up daily
              statistics, and advance the business date from <b>{businessDate}</b> to the next day.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={running}>Cancel</Button>
            <Button onClick={runAudit} disabled={running} className="bg-indigo-600 hover:bg-indigo-700">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Confirm &amp; Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog */}
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" /> Night Audit Complete
            </DialogTitle>
            <DialogDescription>
              Business date advanced {lastResult?.business_date} → {lastResult?.next_business_date}
            </DialogDescription>
          </DialogHeader>
          {lastResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">Room Charges Posted</p><p className="font-semibold">{lastResult.totals?.room_charges_posted}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">Room Revenue</p><p className="font-semibold">{ETB(lastResult.totals?.room_revenue)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">Total Revenue</p><p className="font-semibold">{ETB(lastResult.totals?.total_revenue)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">Occupancy</p><p className="font-semibold">{lastResult.totals?.occupancy_percent}%</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">ADR / RevPAR</p><p className="font-semibold">{ETB(lastResult.totals?.adr)} / {ETB(lastResult.totals?.revpar)}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500 text-xs">No-shows Marked</p><p className="font-semibold">{lastResult.totals?.no_shows_marked}</p></div>
              </div>
              <div className="space-y-1">
                {(lastResult.steps || []).map((st, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="font-medium">{st.step}</span>
                    <Badge variant="secondary" className="text-[10px]">{st.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResultOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
